(() => {
  const drawer = document.getElementById('cart-drawer');
  if (!drawer) return;

  const body = drawer.querySelector('[data-cart-body]');
  const totalEl = drawer.querySelector('[data-cart-total]');
  const checkout = drawer.querySelector('[data-cart-checkout]');
  const shipBar = drawer.querySelector('[data-ship-bar]');
  const shipText = drawer.querySelector('[data-ship-text]');
  const shipFill = drawer.querySelector('[data-ship-fill]');
  const threshold = Number(shipBar?.dataset.threshold || 10000);
  const pointsEl = drawer.querySelector('[data-cart-points]');
  const pointsRate = Number(drawer.dataset.pointsRate || 0);
  const rewardsUrl = drawer.dataset.rewardsUrl || '/pages/rewards';

  let lastFocus = null;
  let busy = false;

  const money = (cents) =>
    new Intl.NumberFormat('en-NZ', { style: 'currency', currency: 'NZD' }).format(cents / 100);

  const thumb = (url, size = 192) =>
    url ? `${url}${url.includes('?') ? '&' : '?'}width=${size}` : '';

  const escape = (value) =>
    String(value).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

  const renderShipping = (total) => {
    const remaining = threshold - total;
    if (remaining > 0) {
      shipBar.classList.remove('cartd__ship--met');
      // Short, because it now shares a row with the drawer title. The old
      // wording ran to two lines beside it on a 360px phone.
      shipText.innerHTML = `<strong>${escape(money(remaining))}</strong> to free delivery`;
      shipFill.style.width = `${Math.min(100, (total / threshold) * 100)}%`;
    } else {
      shipBar.classList.add('cartd__ship--met');
      shipText.textContent = 'Free delivery unlocked';
      shipFill.style.width = '100%';
    }
  };

  const renderLine = (item) => {
    const isGift = Boolean(item.properties && item.properties._gift);
    const variant =
      item.variant_title && item.variant_title !== 'Default Title' ? `<p class="cartd__line-variant">${escape(item.variant_title)}</p>` : '';
    const compare =
      item.original_line_price > item.final_line_price
        ? `<s>${escape(money(item.original_line_price))}</s>`
        : '';

    // Properties prefixed with an underscore are the theme's own bookkeeping.
    // Everything else was typed by the shopper and belongs on screen.
    const notes = Object.entries(item.properties || {})
      .filter(([name, value]) => !name.startsWith('_') && value)
      .map(
        ([name, value]) =>
          `<p class="cartd__line-note"><span>${escape(name)}:</span> ${escape(String(value))}</p>`
      )
      .join('');

    return `
      <div class="cartd__line" data-line-key="${escape(item.key)}">
        ${
          item.image
            ? `<img class="cartd__line-media" src="${escape(thumb(item.image))}" alt="" width="92" height="92" loading="lazy">`
            // Some of the catalogue has no photograph. An img with an empty src
            // draws the browser's broken-image icon, which reads as a fault.
            : '<span class="cartd__line-media cartd__line-media--none" aria-hidden="true"></span>'
        }
        <div>
          <a class="cartd__line-title" href="${escape(item.url)}">${escape(item.product_title)}</a>
          ${variant}
          ${notes}
          ${isGift ? '<span class="cartd__gift">Free gift</span>' : ''}
          <div class="cartd__line-row">
            <span class="cartd__qty">
              <button type="button" data-qty-down aria-label="Reduce quantity"${isGift ? ' disabled' : ''}>&minus;</button>
              <output aria-label="Quantity">${item.quantity}</output>
              <button type="button" data-qty-up aria-label="Increase quantity"${isGift ? ' disabled' : ''}>+</button>
            </span>
            <span class="cartd__line-price">${escape(money(item.final_line_price))}${compare}</span>
          </div>
        </div>
      </div>`;
  };

  const render = (cart) => {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = cart.item_count;
      el.hidden = cart.item_count === 0;
    });

    if (totalEl) totalEl.textContent = money(cart.total_price);
    if (checkout) checkout.disabled = cart.item_count === 0;

    body.innerHTML = cart.items.length
      ? cart.items.map(renderLine).join('')
      : '<p class="cartd__empty">Your cart is empty.</p>';

    renderShipping(cart.total_price);

    // Points belong to the order, so they are quoted against the cart total
    // rather than against a single product's price.
    if (pointsEl) {
      const earned = Math.floor((cart.total_price / 100) * pointsRate);
      pointsEl.innerHTML =
        `Earn <strong>${earned}</strong> reward points on this order` +
        `<a class="cartd__points-help" href="${escape(rewardsUrl)}" aria-label="How reward points work">?</a>`;
      pointsEl.hidden = pointsRate <= 0 || earned <= 0;
    }
  };

  const getCart = async () => {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
      headers: { Accept: 'application/json' }
    });
    return response.json();
  };

  const refresh = async () => {
    const cart = await getCart();
    render(cart);
    return cart;
  };

  const open = () => {
    lastFocus = document.activeElement;
    drawer.hidden = false;
    document.body.style.overflow = 'hidden';
    drawer.querySelector('[data-cart-close]')?.focus();
  };

  const close = () => {
    drawer.hidden = true;
    document.body.style.overflow = '';
    lastFocus?.focus();
  };

  const post = async (path, payload) => {
    const response = await fetch(`${window.Shopify?.routes?.root || '/'}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      // Shopify explains the refusal in the body — "You can only add 2 of
      // this to your cart" — and throwing that away is what made a rejected
      // add look like nothing happening.
      let detail = '';
      try {
        const body = await response.json();
        detail = body.description || body.message || '';
      } catch (parseError) {
        /* not every failure carries a JSON body */
      }
      const error = new Error(detail || `${path} failed with ${response.status}`);
      error.status = response.status;
      error.detail = detail;
      throw error;
    }

    return response.json();
  };

  const add = async (variantId, quantity = 1, extras = []) => {
    const items = [{ id: Number(variantId), quantity }, ...extras];
    await post('cart/add.js', { items });
    await refresh();
    open();
  };

  const setQuantity = async (key, quantity) => {
    // Returns the cart, because Shopify caps a line at the stock it has and
    // answers 200 with the capped number rather than refusing. Without reading
    // it back, pressing + at the ceiling looked like a dead button.
    const cart = await post('cart/change.js', { id: key, quantity });
    await refresh();
    return cart;
  };

  const notice = drawer.querySelector('[data-cart-notice]');

  const say = (message) => {
    if (!notice) return;
    notice.textContent = message || '';
    notice.hidden = !message;
  };

  // Removes every line of a variant, whatever properties each line carries.
  // change.js addresses one line at a time and cannot do this.
  const clearVariant = async (variantId) => {
    await post('cart/update.js', { updates: { [String(variantId)]: 0 } });
  };

  // ---------- delivery instructions ----------

  const noteField = drawer.querySelector('[data-cart-note]');
  const noteState = drawer.querySelector('[data-cart-note-state]');

  if (noteField) {
    let saveTimer = null;
    let lastSaved = noteField.value;

    // The markup opens the box only when a note is already written, which is
    // the state a phone needs: open, the label and a three row field pushed the
    // subtotal and the checkout button below the fold. A desktop drawer has the
    // room, so it opens there and the shopper sees the field without a tap.
    const wrap = drawer.querySelector('[data-cart-note-wrap]');
    const noteTag = drawer.querySelector('[data-cart-note-tag]');
    if (wrap && window.matchMedia('(min-width: 900px)').matches) wrap.open = true;

    const saveNote = async () => {
      const note = noteField.value;
      if (note === lastSaved) return;

      try {
        await post('cart/update.js', { note });
        lastSaved = note;
        if (noteState) noteState.textContent = note ? 'Saved' : '';
        // Closed again, the tag is the only thing saying a note is in there.
        if (noteTag) noteTag.hidden = note.trim() === '';
      } catch (error) {
        if (noteState) noteState.textContent = 'Could not save that just now.';
      }
    };

    // Saved as they type, but not on every keystroke: one request once they
    // pause, and one more on blur in case they close the drawer mid-thought.
    noteField.addEventListener('input', () => {
      if (noteState) noteState.textContent = '';
      clearTimeout(saveTimer);
      saveTimer = setTimeout(saveNote, 700);
    });

    noteField.addEventListener('blur', () => {
      clearTimeout(saveTimer);
      saveNote();
    });
  }

  document.addEventListener('click', async (event) => {
    const addButton = event.target.closest('.pcard__add');
    if (addButton) {
      event.preventDefault();
      if (busy) return;
      busy = true;
      addButton.dataset.state = 'busy';
      try {
        await add(addButton.dataset.variantId);
        addButton.dataset.state = 'done';
        setTimeout(() => delete addButton.dataset.state, 1200);
      } catch (error) {
        delete addButton.dataset.state;
        console.error(error);
      } finally {
        busy = false;
      }
      return;
    }

    if (event.target.closest('[data-cart-toggle]')) {
      event.preventDefault();
      // Open first, reconcile after. This used to await /cart.js before showing
      // anything, so tapping the cart did nothing for as long as the round trip
      // took and the drawer felt broken on a slow connection. The drawer is
      // already rendered from Liquid and kept current by every add and quantity
      // change, so what appears is right; the fetch only catches a change made
      // in another tab.
      open();
      refresh().catch(() => {});
      return;
    }

    if (event.target.closest('[data-cart-close]')) {
      event.preventDefault();
      close();
      return;
    }

    const line = event.target.closest('.cartd__line');
    if (!line || busy) return;
    const key = line.dataset.lineKey;
    const current = Number(line.querySelector('output').textContent);

    const up = event.target.closest('[data-qty-up]');
    const down = event.target.closest('[data-qty-down]');
    if (!up && !down) return;

    const wanted = up ? current + 1 : Math.max(0, current - 1);
    busy = true;

    try {
      const cart = await setQuantity(key, wanted);
      const line = cart.items.find((item) => item.key === key);

      // Shopify silently caps rather than refusing, so the shortfall has to be
      // read back and said out loud.
      if (up && line && line.quantity < wanted) {
        say(
          line.quantity > 0
            ? `Only ${line.quantity} of this left in stock.`
            : 'That is out of stock.'
        );
      } else {
        say('');
      }
    } catch (error) {
      // Shopify answers a refused increase with "Only 23 items were added to
      // your cart due to availability", which is about an add the shopper did
      // not make. They pressed plus; the useful fact is the ceiling.
      const ceiling = /availability|only \d+/i.test(error.detail || '');
      say(
        up && ceiling
          ? `Only ${current} of this left in stock.`
          : error.detail || 'That could not be updated just now.'
      );
    } finally {
      busy = false;
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !drawer.hidden) close();
  });

  window.StLukesCart = { add, setQuantity, clearVariant, refresh, open, close };

  refresh().catch(() => {
    body.innerHTML = '<p class="cartd__empty">Your cart is empty.</p>';
  });
})();
